import clsx from 'clsx';
import { Copy, PanelBottom, PanelRight, Plus, X } from 'lucide-react';
import { cleanThreadTitle } from './threadTitle';

export type JamesDockMode = 'right' | 'bottom' | 'floating';

interface JamesChatHeaderProps {
  title?: string | null;
  onNewThread?: () => void;
  onClose?: () => void;
  showClose?: boolean;
  dockMode?: JamesDockMode;
  onDockModeChange?: (mode: JamesDockMode) => void;
}

export function JamesChatHeader({
  title: rawTitle,
  onNewThread,
  onClose,
  showClose = true,
  dockMode,
  onDockModeChange,
}: JamesChatHeaderProps) {
  const title = cleanThreadTitle(rawTitle);
  const showDockControls = dockMode !== undefined && onDockModeChange !== undefined;
  const compact = dockMode !== undefined;

  if (compact) {
    return (
      <div className="flex-shrink-0 border-b border-border-light bg-surface-primary">
        <div className="flex items-center gap-2 px-3 py-2">
          <h2 className="min-w-0 flex-1 truncate text-body-sm font-medium text-text-primary" title={title || undefined}>
            {title || 'James'}
          </h2>

          <div className="flex flex-shrink-0 items-center gap-1.5">
            {showDockControls && (
              <div className="inline-flex items-center overflow-hidden rounded-md border border-border-light bg-surface-primary">
                <DockButton
                  active={dockMode === 'right'}
                  onClick={() => onDockModeChange!('right')}
                  title="Dock right"
                  label="Dock right"
                >
                  <PanelRight className="h-3 w-3" />
                </DockButton>
                <DockButton
                  active={dockMode === 'bottom'}
                  onClick={() => onDockModeChange!('bottom')}
                  title="Dock bottom"
                  label="Dock bottom"
                >
                  <PanelBottom className="h-3 w-3" />
                </DockButton>
                <DockButton
                  active={dockMode === 'floating'}
                  onClick={() => onDockModeChange!('floating')}
                  title="Floating window"
                  label="Floating window"
                >
                  <Copy className="h-3 w-3" />
                </DockButton>
              </div>
            )}
            {onNewThread && (
              <button
                onClick={onNewThread}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-surface-secondary hover:text-text-primary"
                title="New conversation"
                aria-label="New conversation"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            )}
            {showClose && onClose && (
              <button
                onClick={onClose}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-surface-secondary hover:text-text-primary"
                title="Close"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-shrink-0 border-b border-border-light bg-surface-primary">
      <div className="flex items-center gap-3 px-6 py-3">
        <h2 className="min-w-0 flex-1 truncate text-heading-sm font-medium text-text-primary" title={title || undefined}>
          {title || 'New conversation'}
        </h2>

        <div className="flex flex-shrink-0 items-center gap-1">
          {onNewThread && (
            <button
              onClick={onNewThread}
              className="btn btn-ghost h-8 px-3 text-body-xs"
              title="New conversation"
            >
              New thread
            </button>
          )}
          {showClose && onClose && (
            <button
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-surface-secondary hover:text-text-primary"
              title="Close"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface DockButtonProps {
  active: boolean;
  onClick: () => void;
  title: string;
  label: string;
  children: React.ReactNode;
}

function DockButton({ active, onClick, title, label, children }: DockButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={label}
      aria-pressed={active}
      className={clsx(
        'flex h-7 w-7 items-center justify-center transition-colors',
        active
          ? 'bg-surface-inverse text-text-inverse'
          : 'text-text-secondary hover:bg-surface-secondary hover:text-text-primary'
      )}
    >
      {children}
    </button>
  );
}
